/* eslint-disable eqeqeq */
/**
 * Phase 6e2b-1 — §51.4 mounting model math as PURE FUNCTIONS (parity 333).
 * Source: accessories/mounting/model/mounting.js (228 lines, read in full).
 *
 * The legacy MountingModel is a client-side derived model (no URL): it maps
 * position(x, y) + section airway dims + accessory calc size into the 6
 * distance fields and back. Formulas copied VERBATIM — including the
 * intentional floor/roof height/width swap (the accessory lies flat on
 * horizontal planes, so the "height" of the shape consumes airway WIDTH):
 *   floor/roof: distance_to_wall_right = aw_width − HEIGHT_calc − y
 *               distance_to_wall_downstream = aw_length − WIDTH_calc − x
 *
 * aw_width/aw_height are the legacy section model's front-end derivations
 * (section.js:107/:122): tunnel.width − 2×casing_insulation_thick and
 * tunnel.height − casing_insulation_thick.
 */

export interface MountingSectionDims {
  aw_width: number;
  aw_height: number;
  aw_length: number;
}

export interface MountingAccessorySize {
  width_calc: number;
  height_calc: number;
}

export interface MountingDistances {
  distance_to_wall_left: number;
  distance_to_wall_right: number;
  distance_to_wall_upstream: number;
  distance_to_wall_downstream: number;
  distance_to_roof: number;
  distance_to_floor: number;
}

export const ZERO_DISTANCES: MountingDistances = {
  distance_to_wall_left: 0.0,
  distance_to_wall_right: 0.0,
  distance_to_wall_upstream: 0.0,
  distance_to_wall_downstream: 0.0,
  distance_to_roof: 0.0,
  distance_to_floor: 0.0,
};

/** section.js:107/:122 — aw dims derived on the front-end from the tunnel. */
export function deriveAwDims(
  section: { aw_length?: unknown; aw_width?: unknown; aw_height?: unknown; casing_insulation_thick?: unknown },
  tunnel?: { width?: unknown; height?: unknown } | null
): MountingSectionDims {
  const thickness = Number(section.casing_insulation_thick) || 0;
  let aw_width = Number(section.aw_width) || 0;
  let aw_height = Number(section.aw_height) || 0;
  if (tunnel && tunnel.width != null) {
    aw_width = thickness ? Number(tunnel.width) - thickness * 2 : Number(tunnel.width);
  }
  if (tunnel && tunnel.height != null) {
    aw_height = thickness ? Number(tunnel.height) - thickness : Number(tunnel.height);
  }
  return { aw_width, aw_height, aw_length: Number(section.aw_length) || 0 };
}

/**
 * mounting.js fetch() (:82-136) — position (x,y) → the 6 distances for the
 * given plane family. Unknown plane → null result (legacy sets plane null).
 */
export function positionToDistances(
  planeValue: string | null | undefined,
  x: number,
  y: number,
  section: MountingSectionDims,
  accessory: MountingAccessorySize
): MountingDistances | null {
  if (!planeValue) return null;
  const attrs: MountingDistances = { ...ZERO_DISTANCES };
  if (planeValue === 'wall_left' || planeValue === 'wall_right') {
    attrs.distance_to_wall_left = 0.0;
    attrs.distance_to_wall_right = 0.0;
    attrs.distance_to_wall_upstream = x;
    attrs.distance_to_wall_downstream = section.aw_length - accessory.width_calc - x;
    attrs.distance_to_floor = y;
    attrs.distance_to_roof = section.aw_height - accessory.height_calc - y;
  } else if (planeValue === 'wall_upstream' || planeValue === 'wall_downstream') {
    attrs.distance_to_wall_upstream = 0.0;
    attrs.distance_to_wall_downstream = 0.0;
    attrs.distance_to_wall_left = x;
    attrs.distance_to_wall_right = section.aw_width - accessory.width_calc - x;
    attrs.distance_to_floor = y;
    attrs.distance_to_roof = section.aw_height - accessory.height_calc - y;
  } else if (planeValue === 'floor' || planeValue === 'roof') {
    attrs.distance_to_floor = 0.0;
    attrs.distance_to_roof = 0.0;
    attrs.distance_to_wall_upstream = x;
    attrs.distance_to_wall_downstream = section.aw_length - accessory.width_calc - x;
    attrs.distance_to_wall_left = y;
    // VERBATIM height/width swap (mounting.js:124-125): horizontal planes
    // consume airway width with the accessory HEIGHT.
    attrs.distance_to_wall_right = section.aw_width - accessory.height_calc - y;
  } else {
    return null;
  }
  return attrs;
}

/**
 * mounting.js recalc_position() (:139-162) — distances → {mounting_plane_id,
 * x, y} (the position model write; its auto-save persists it).
 */
export function distancesToPosition(
  planeValue: string | null | undefined,
  planeId: number | null | undefined,
  d: MountingDistances
): { mounting_plane_id: number; x: number; y: number } | null {
  if (!planeValue || planeId == null) return null;
  if (planeValue === 'wall_left' || planeValue === 'wall_right') {
    return { mounting_plane_id: planeId, x: d.distance_to_wall_upstream, y: d.distance_to_floor };
  }
  if (planeValue === 'wall_upstream' || planeValue === 'wall_downstream') {
    return { mounting_plane_id: planeId, x: d.distance_to_wall_left, y: d.distance_to_floor };
  }
  if (planeValue === 'floor' || planeValue === 'roof') {
    return { mounting_plane_id: planeId, x: d.distance_to_wall_upstream, y: d.distance_to_wall_left };
  }
  return null;
}

/**
 * mounting.js recalc_output_fields() (:164-213) — complementary-distance
 * recomputation per plane family (the "edit one distance, the opposite one
 * recomputes" popup behavior). Returns a fresh distances object.
 */
export function recalcOutputFields(
  planeValue: string | null | undefined,
  d: MountingDistances,
  section: MountingSectionDims,
  accessory: MountingAccessorySize
): MountingDistances {
  if (planeValue === 'wall_left' || planeValue === 'wall_right') {
    return {
      distance_to_wall_left: 0.0,
      distance_to_wall_right: 0.0,
      distance_to_wall_upstream: d.distance_to_wall_upstream,
      distance_to_floor: d.distance_to_floor,
      distance_to_wall_downstream:
        section.aw_length - d.distance_to_wall_upstream - accessory.width_calc,
      distance_to_roof: section.aw_height - d.distance_to_floor - accessory.height_calc,
    };
  }
  if (planeValue === 'wall_upstream' || planeValue === 'wall_downstream') {
    return {
      distance_to_wall_upstream: 0.0,
      distance_to_wall_downstream: 0.0,
      distance_to_wall_left: d.distance_to_wall_left,
      distance_to_wall_right:
        section.aw_width - d.distance_to_wall_left - accessory.width_calc,
      distance_to_floor: d.distance_to_floor,
      distance_to_roof: section.aw_height - accessory.height_calc - d.distance_to_floor,
    };
  }
  if (planeValue === 'floor' || planeValue === 'roof') {
    return {
      distance_to_floor: 0.0,
      distance_to_roof: 0.0,
      distance_to_wall_upstream: d.distance_to_wall_upstream,
      distance_to_wall_downstream:
        section.aw_length - d.distance_to_wall_upstream - accessory.width_calc,
      distance_to_wall_left: d.distance_to_wall_left,
      // VERBATIM swap again (:198-200).
      distance_to_wall_right: section.aw_width - d.distance_to_wall_left - accessory.height_calc,
    };
  }
  return { ...d };
}
